import { useState, useRef, useEffect, type ReactNode } from "react";
import { Tabbar, type TabId } from "./Tabbar";

interface TabContainerProps {
  homeTab: ReactNode;
  challengesTab: ReactNode;
  createTab: ReactNode;
  profileTab: ReactNode;
}

// Scroll positions per tab
const scrollPositions: Record<TabId, number> = {
  home: 0,
  challenges: 0,
  create: 0,
  profile: 0,
};

function TabContainer({ homeTab, challengesTab, createTab, profileTab }: TabContainerProps) {
  const [activeTab, setActiveTab] = useState<TabId>("home");
  const containerRef = useRef<HTMLDivElement>(null);

  // Save scroll position when switching tabs
  const handleTabChange = (newTab: TabId) => {
    // Save current scroll position
    scrollPositions[activeTab] = window.scrollY;

    setActiveTab(newTab);
  };

  // Restore scroll position when tab changes
  useEffect(() => {
    // Use requestAnimationFrame to ensure DOM is updated
    requestAnimationFrame(() => {
      window.scrollTo(0, scrollPositions[activeTab]);
    });
  }, [activeTab]);

  // Hide Telegram back button on tab screens
  useEffect(() => {
    const webApp = window.Telegram?.WebApp;
    if (!webApp?.BackButton) return;

    const timeoutId = setTimeout(() => {
      webApp.BackButton.hide();
    }, 50);

    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <div className="tab-container" ref={containerRef}>
      {/* All tabs render simultaneously, only active one visible */}
      <div
        className="tab-panel"
        style={{ display: activeTab === "home" ? "block" : "none" }}
        aria-hidden={activeTab !== "home"}
      >
        {homeTab}
      </div>
      <div
        className="tab-panel"
        style={{ display: activeTab === "challenges" ? "block" : "none" }}
        aria-hidden={activeTab !== "challenges"}
      >
        {challengesTab}
      </div>
      <div
        className="tab-panel"
        style={{ display: activeTab === "create" ? "block" : "none" }}
        aria-hidden={activeTab !== "create"}
      >
        {createTab}
      </div>
      <div
        className="tab-panel"
        style={{ display: activeTab === "profile" ? "block" : "none" }}
        aria-hidden={activeTab !== "profile"}
      >
        {profileTab}
      </div>

      <Tabbar activeTab={activeTab} onTabChange={handleTabChange} />
    </div>
  );
}

export default TabContainer;
